/**
 * Betreiberportal für den Bauhof.
 *
 * Eigener Anmeldeweg, eigenes Cookie, eigener Datenpfad – bewusst nicht in den
 * Rahmen des Prüfbüros eingehängt. Der Bauhof sieht offene Mängel seines
 * eigenen Kunden und die abgeschlossenen Berichte.
 *
 * Was er nicht kann: einen Mangel für behoben erklären. Er meldet die
 * Behebung, bestätigen darf nur der Prüfer bei der Nachprüfung. Das steht
 * deshalb auch so auf dem Bildschirm – sonst wäre die Erwartung eine andere.
 */

import { useCallback, useEffect, useState, type FormEvent } from 'react'
import { ApiFehler, api } from '../api/klient'
import { STUFENTEXT, stufenklasse, type Mangelstatus, type Stufe } from '../api/typen'
import { Eigenkontrolle } from './Eigenkontrolle'
import { ErstesPasswort } from './ErstesPasswort'
import { Fehlerhinweis, Ladeanzeige, Leerhinweis, datumDeutsch } from '../bausteine/Hinweise'
import logo from '../assets/spuer-logo.png'
import wortmarke from '../assets/spuer-wortmarke.png'
import { Profil } from './Profil'

export type PortalKonto = {
  id: string
  email: string
  name: string
  rolle: 'leitung' | 'mitarbeiter'
  kunde_id: string
  kunde_name: string
  /** Das Passwort kam per Mail und gilt nur für die erste Anmeldung. */
  passwort_einmalig: boolean
  /** Das Prüfbüro sieht hier nur zu; geschrieben wird nichts. */
  wird_angesehen?: boolean
}

type Mangel = {
  id: string
  spielplatz_id: string
  spielplatz_name: string
  geraet_bezeichnung: string
  kennung: string | null
  geraetenummer: string | null
  geraetetyp: string | null
  stufe: Stufe | null
  mangel: string | null
  bemerkung: string | null
  normverweis: string | null
  gesperrt: boolean
  mangelstatus: Mangelstatus | null
  /** Erwartet der Prüfer eine Rückmeldung zur Behebung? */
  meldung_erwartet: boolean
  frist_am: string | null
  festgestellt_am: string
  berichtsnummer: string | null
  foto_ids: string[]
  gemeldet_am: string | null
  meldungstext: string | null
}

type Bericht = {
  id: string
  berichtsnummer: string | null
  spielplatz_name: string
  pruefart: string
  datum: string
  naechste_pruefung_am: string | null
}

type Uebersicht = {
  maengel: Mangel[]
  gesperrte_geraete: number
  offene_maengel: number
  gemeldete_maengel: number
  berichte: Bericht[]
}

export function Portal() {
  const [konto, setzeKonto] = useState<PortalKonto | null>(null)
  const [zustand, setzeZustand] = useState<'prueft' | 'angemeldet' | 'abgemeldet'>('prueft')

  const laden = useCallback(async () => {
    try {
      setzeKonto(await api.holen<PortalKonto>('/api/portal/ich'))
      setzeZustand('angemeldet')
    } catch {
      setzeZustand('abgemeldet')
    }
  }, [])

  useEffect(() => {
    /* Steht ein Anmeldelink in der Adresse, wird er zuerst eingelöst.

       Und danach sofort aus der Adresse entfernt: sonst stünde er in der
       Verlaufsliste des Browsers, in jedem Lesezeichen und in jeder
       weitergeleiteten Adresse. Verbraucht ist er dann zwar – aber ein
       Zugangstoken, das irgendwo sichtbar herumliegt, gewöhnt Leute an
       etwas Falsches. */
    const adresse = new URL(window.location.href)
    const token = adresse.searchParams.get('anmeldung')

    if (!token) {
      void laden()
      return
    }

    void (async () => {
      try {
        await api.senden('/api/portal/anmeldelink/einloesen', { token })
      } catch {
        // Abgelaufen oder schon benutzt: dann greift die gewöhnliche
        // Anmeldung, und die sagt, was zu tun ist.
      } finally {
        adresse.searchParams.delete('anmeldung')
        window.history.replaceState({}, '', adresse.toString())
        void laden()
      }
    })()
  }, [laden])

  if (zustand === 'prueft') return <Ladeanzeige text="Prüft die Anmeldung …" />
  if (zustand === 'abgemeldet') return <PortalAnmeldung fertig={laden} />

  return (
    <PortalUebersicht
      konto={konto!}
      abmelden={() => void abmelden(setzeZustand)}
      kontoGeaendert={setzeKonto}
    />
  )
}

async function abmelden(setzeZustand: (z: 'abgemeldet') => void) {
  try {
    await api.senden('/api/portal/abmelden')
  } catch {
    // Auch bei einem Fehler lokal abmelden.
  }
  setzeZustand('abgemeldet')
}

function PortalAnmeldung({ fertig }: { fertig: () => void }) {
  const [laeuft, setzeLaeuft] = useState(false)
  const [fehler, setzeFehler] = useState<unknown>(null)
  /* Anmeldung ohne Passwort.

     Der Bauhof meldet sich viermal im Jahr an. Ein Passwort, das man
     viermal im Jahr braucht, hat man nicht im Kopf – es steht auf einem
     Zettel am Monitor oder wird jedes Mal zurückgesetzt. Ein Link, der
     einmal gilt und fünfzehn Minuten lebt, ist in dieser Lage nicht
     schwächer, sondern stärker. */
  const [ohnePasswort, setzeOhnePasswort] = useState(false)
  const [verschickt, setzeVerschickt] = useState(false)
  /* Zweiter Schritt. Hat der Zugang einen zweiten Faktor, stellt der
     Server noch keine Sitzung aus – hier steht dann nur dieses Formular. */
  const [zweitschritt, setzeZweitschritt] = useState<{
    schritt: string
    arten: string[]
  } | null>(null)
  const [code, setzeCode] = useState('')
  const [mailLaeuft, setzeMailLaeuft] = useState(false)

  async function linkAnfordern(ereignis: FormEvent<HTMLFormElement>) {
    ereignis.preventDefault()
    const f = new FormData(ereignis.currentTarget)
    setzeFehler(null)
    setzeLaeuft(true)
    try {
      await api.senden('/api/portal/anmeldelink', {
        email: String(f.get('email')),
      })
      setzeVerschickt(true)
    } catch (x) {
      setzeFehler(x)
    } finally {
      setzeLaeuft(false)
    }
  }

  async function absenden(ereignis: FormEvent<HTMLFormElement>) {
    ereignis.preventDefault()
    const f = new FormData(ereignis.currentTarget)
    setzeFehler(null)
    setzeLaeuft(true)
    try {
      const antwort = await api.senden<{
        zweiter_faktor?: boolean
        schritt?: string
        arten?: string[]
      }>('/api/portal/anmelden', {
        email: String(f.get('email')),
        passwort: String(f.get('passwort')),
      })
      if (antwort.zweiter_faktor) {
        setzeZweitschritt({ schritt: antwort.schritt!, arten: antwort.arten! })
        return
      }
      fertig()
    } catch (x) {
      setzeFehler(x)
    } finally {
      setzeLaeuft(false)
    }
  }

  return (
    <div className="mx-auto flex min-h-dvh max-w-md flex-col justify-center p-4">
      <header className="mb-6 text-center">
        <img src={logo} alt="SPÜR" className="mx-auto mb-3 w-full max-w-[16rem]" />
        <h1 className="text-2xl font-bold text-slate-900">Kundenportal</h1>
        <p className="mt-1 text-slate-700">
          Mängel und Prüfberichte Ihrer Spielplätze
        </p>
      </header>

      {zweitschritt ? (
        <form
          className="karte space-y-4"
          onSubmit={async (e) => {
            e.preventDefault()
            setzeFehler(null)
            setzeLaeuft(true)
            try {
              await api.senden('/api/portal/anmelden/zweitfaktor', {
                schritt: zweitschritt.schritt,
                code: code.trim(),
              })
              fertig()
            } catch (x) {
              setzeFehler(x)
            } finally {
              setzeLaeuft(false)
            }
          }}
        >
          <div>
            <h2 className="text-lg font-bold">Zweiter Schritt</h2>
            <p className="mt-1 text-base text-slate-700">
              {zweitschritt.arten.includes('app')
                ? 'Geben Sie den Code aus Ihrer Authenticator-App ein.'
                : 'Wir schicken Ihnen einen Code per E-Mail.'}
            </p>
          </div>

          <div>
            <label htmlFor="p-code">Code</label>
            <input
              id="p-code"
              value={code}
              onChange={(e) => setzeCode(e.target.value)}
              required
              autoFocus
              autoComplete="one-time-code"
              spellCheck={false}
              placeholder="123456"
            />
            <p className="mt-1 text-base text-slate-600">
              Ein Wiederherstellungscode geht hier ebenso.
            </p>
          </div>

          <Fehlerhinweis fehler={fehler} />

          <button
            type="submit"
            className="knopf-haupt w-full"
            disabled={laeuft || !code.trim()}
          >
            {laeuft ? 'Prüft …' : 'Anmelden'}
          </button>

          {zweitschritt.arten.includes('mail') && (
            <button
              type="button"
              className="knopf-still w-full"
              disabled={mailLaeuft}
              onClick={async () => {
                setzeMailLaeuft(true)
                try {
                  await api.senden('/api/portal/anmelden/zweitfaktor/mail', {
                    schritt: zweitschritt.schritt,
                  })
                } catch (x) {
                  setzeFehler(x)
                } finally {
                  setzeMailLaeuft(false)
                }
              }}
            >
              {mailLaeuft ? 'Schickt …' : 'Code per E-Mail schicken'}
            </button>
          )}

          <button
            type="button"
            className="knopf-still w-full"
            onClick={() => {
              setzeZweitschritt(null)
              setzeCode('')
              setzeFehler(null)
            }}
          >
            Abbrechen
          </button>
        </form>
      ) : verschickt ? (
        <div className="karte space-y-2">
          <p className="font-semibold">Wir haben Ihnen einen Link geschickt.</p>
          <p>
            Sehen Sie in Ihrem Postfach nach. Der Link gilt 15 Minuten und nur
            ein einziges Mal.
          </p>
          <p className="text-base text-slate-600">
            Nichts angekommen? Sehen Sie im Spam-Ordner nach, oder melden Sie
            sich mit Ihrem Passwort an.
          </p>
          <button
            type="button"
            className="knopf-still w-full"
            onClick={() => {
              setzeVerschickt(false)
              setzeOhnePasswort(false)
            }}
          >
            Zurück
          </button>
        </div>
      ) : (
        <form
          onSubmit={ohnePasswort ? linkAnfordern : absenden}
          className="karte space-y-4"
        >
          <div>
            <label htmlFor="email">E-Mail-Adresse</label>
            <input
              id="email"
              name="email"
              type="email"
              required
              autoComplete="username"
              autoCapitalize="none"
            />
          </div>

          {!ohnePasswort && (
            <div>
              <label htmlFor="passwort">Passwort</label>
              <input
                id="passwort"
                name="passwort"
                type="password"
                required
                autoComplete="current-password"
              />
            </div>
          )}

          <Fehlerhinweis fehler={fehler} />

          <button type="submit" className="knopf-haupt w-full" disabled={laeuft}>
            {laeuft
              ? ohnePasswort
                ? 'Schickt …'
                : 'Meldet an …'
              : ohnePasswort
                ? 'Link schicken'
                : 'Anmelden'}
          </button>

          <button
            type="button"
            className="knopf-still w-full"
            onClick={() => {
              setzeOhnePasswort((o) => !o)
              setzeFehler(null)
            }}
          >
            {ohnePasswort
              ? 'Doch mit Passwort anmelden'
              : 'Ohne Passwort: Link per E-Mail'}
          </button>
        </form>
      )}

      <p className="mt-4 text-center text-base text-slate-600">
        Zugänge richtet das Prüfbüro ein.
      </p>
    </div>
  )
}

function PortalUebersicht({
  konto,
  abmelden,
  kontoGeaendert,
}: {
  konto: PortalKonto
  abmelden: () => void
  /** Nach dem Ändern der eigenen Daten – der Name steht auch in der Kopfzeile. */
  kontoGeaendert: (neu: PortalKonto) => void
}) {
  const [daten, setzeDaten] = useState<Uebersicht | null>(null)
  const [profilOffen, setzeProfilOffen] = useState(false)
  const [fehler, setzeFehler] = useState<unknown>(null)

  const laden = useCallback(async () => {
    try {
      setzeDaten(await api.holen<Uebersicht>('/api/portal/uebersicht'))
    } catch (f) {
      setzeFehler(f)
    }
  }, [])

  useEffect(() => {
    void laden()
  }, [laden])

  if (fehler) return <Fehlerhinweis fehler={fehler} />

  /* Einmalpasswort: nichts als die Passwortänderung. Der Server lässt auch
     nichts anderes zu — die Übersicht bliebe leer und die Fehlermeldung
     wäre das Einzige, was zu sehen ist. */
  if (konto.passwort_einmalig) {
    return (
      <ErstesPasswort
        konto={konto}
        // Das Konto neu holen, nicht die Übersicht: das Kennzeichen
        // `passwort_einmalig` steht am Konto, und ohne dieses Nachladen
        // bliebe die Seite nach dem Wechsel stehen.
        fertig={async () => kontoGeaendert(await api.holen<PortalKonto>('/api/portal/ich'))}
        abmelden={abmelden}
      />
    )
  }

  if (!daten) return <Ladeanzeige />

  return (
    <div className="flex min-h-dvh flex-col">
      {/* Ohne diesen Streifen sähe jemand ein Portal, in dem alle Knöpfe
          abgewiesen werden, und hielte das für einen Fehler. */}
      {konto.wird_angesehen && (
        <div className="bg-stufe-2 px-4 py-2 text-center font-semibold text-white">
          Ansicht des Prüfbüros: Sie sehen das Portal von {konto.name} (
          {konto.kunde_name}). Änderungen sind hier nicht möglich — über
          „Abmelden" kommen Sie zurück.
        </div>
      )}

      <header className="border-b-2 border-slate-200 bg-white">
        <div aria-hidden className="h-[3px] bg-akzent-500" />
        <div className="mx-auto flex max-w-4xl items-center gap-3 p-4">
          <img src={wortmarke} alt="SPÜR" className="h-8 w-auto shrink-0" />
          <div className="min-w-0 flex-1">
            <div className="font-semibold text-slate-900">Kundenportal</div>
            <div className="truncate text-base text-slate-700">{konto.kunde_name}</div>
          </div>
          {/* Auf dem Handy nur das Zeichen – der Name des Kontos steht im
              Blatt selbst, und der Platz oben ist knapp. */}
          <button
            type="button"
            className="knopf-still px-3"
            onClick={() => setzeProfilOffen(true)}
            title="Meine Daten"
          >
            <span aria-hidden className="text-lg leading-none">
              ☺
            </span>
            <span className="hidden sm:inline">Meine Daten</span>
            <span className="nur-vorlesen sm:hidden">Meine Daten</span>
          </button>
          <button type="button" className="knopf-still px-3" onClick={abmelden}>
            Abmelden
          </button>
        </div>
      </header>

      <main className="mx-auto w-full max-w-4xl flex-1 space-y-6 p-4">
        <div className="grid grid-cols-3 gap-3">
          <div className={`karte ${daten.offene_maengel > 0 ? 'border-stufe-3' : ''}`}>
            <div
              className={`text-3xl font-bold ${
                daten.offene_maengel > 0 ? 'text-stufe-3' : 'text-marke-700'
              }`}
            >
              {daten.offene_maengel}
            </div>
            <div className="text-base text-slate-700">offene Mängel</div>
          </div>
          <div className="karte">
            <div className="text-3xl font-bold text-marke-700">{daten.gemeldete_maengel}</div>
            <div className="text-base text-slate-700">gemeldet, noch nicht bestätigt</div>
          </div>
          <div className={`karte ${daten.gesperrte_geraete > 0 ? 'border-stufe-3' : ''}`}>
            <div
              className={`text-3xl font-bold ${
                daten.gesperrte_geraete > 0 ? 'text-stufe-3' : 'text-marke-700'
              }`}
            >
              {daten.gesperrte_geraete}
            </div>
            <div className="text-base text-slate-700">gesperrte Geräte</div>
          </div>
        </div>

        {daten.gesperrte_geraete > 0 && (
          <div className="rounded-feld border-2 border-stufe-3 bg-stufe-3-grund p-3 font-semibold text-stufe-3">
            Gesperrte Geräte dürfen nicht benutzt werden. Bitte absperren oder abbauen,
            bis der Mangel behoben und von uns bestätigt ist.
          </div>
        )}

        <section>
          <h2 className="ueberschrift-akzent mb-3 text-xl">Mängel</h2>
          {daten.maengel.length === 0 ? (
            <Leerhinweis
              titel="Keine offenen Mängel"
              text="Alle festgestellten Mängel sind behoben und bestätigt."
            />
          ) : (
            <ul className="space-y-3">
              {daten.maengel.map((m) => (
                <MangelKarte key={m.id} mangel={m} neuLaden={laden} />
              ))}
            </ul>
          )}
        </section>

        <Eigenkontrolle />

        <section>
          <h2 className="ueberschrift-akzent mb-3 text-xl">Prüfberichte</h2>
          {daten.berichte.length === 0 ? (
            <Leerhinweis titel="Noch kein Bericht" />
          ) : (
            <ul className="space-y-2">
              {daten.berichte.map((b) => (
                <li key={b.id} className="karte flex flex-wrap items-center gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold">
                      {b.berichtsnummer} · {b.spielplatz_name}
                    </div>
                    <div className="text-base text-slate-700">
                      Geprüft am {datumDeutsch(b.datum)}
                      {b.naechste_pruefung_am
                        ? ` · nächste Prüfung bis ${datumDeutsch(b.naechste_pruefung_am)}`
                        : ''}
                    </div>
                  </div>
                  <a
                    className="knopf-zweit px-3"
                    href={`/api/portal/berichte/${b.id}.pdf`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    PDF ansehen
                  </a>
                </li>
              ))}
            </ul>
          )}
        </section>
      </main>

      {profilOffen && (
        <Profil
          konto={konto}
          schliessen={() => setzeProfilOffen(false)}
          aktualisiert={kontoGeaendert}
        />
      )}
    </div>
  )
}

function MangelKarte({ mangel, neuLaden }: { mangel: Mangel; neuLaden: () => void }) {
  const [formular, setzeFormular] = useState(false)
  const [text, setzeText] = useState('')
  const [laeuft, setzeLaeuft] = useState(false)
  const [fehler, setzeFehler] = useState<unknown>(null)

  const gemeldet = mangel.mangelstatus === 'gemeldet'

  async function melden(ereignis: FormEvent) {
    ereignis.preventDefault()
    setzeFehler(null)
    setzeLaeuft(true)
    try {
      await api.senden(`/api/portal/maengel/${mangel.id}/behebung`, {
        text: text.trim() || null,
      })
      setzeFormular(false)
      neuLaden()
    } catch (f) {
      setzeFehler(f)
      if (f instanceof ApiFehler && f.status === 401) window.location.reload()
    } finally {
      setzeLaeuft(false)
    }
  }

  return (
    <li className={`karte ${mangel.gesperrt ? 'border-stufe-3' : ''}`}>
      <div className="flex flex-wrap items-start gap-3">
        <span className={`${stufenklasse(mangel.stufe)} px-3 text-lg`}>
          {mangel.stufe ?? '–'}
        </span>

        <div className="min-w-0 flex-1">
          <div className="font-semibold">
            {mangel.kennung ? `${mangel.kennung} · ` : ''}
            {mangel.geraet_bezeichnung}
          </div>
          <div className="text-base text-slate-700">
            {mangel.spielplatz_name}
            {mangel.geraetetyp ? ` · ${mangel.geraetetyp}` : ''}
          </div>
          <p className="mt-1">{mangel.mangel}</p>
          {mangel.bemerkung && (
            <p className="text-base text-slate-700">{mangel.bemerkung}</p>
          )}
          <div className="mt-1 text-base text-slate-600">
            {mangel.stufe ? STUFENTEXT[mangel.stufe] : ''}
            {mangel.normverweis ? ` · ${mangel.normverweis}` : ''}
            {` · festgestellt ${datumDeutsch(mangel.festgestellt_am)}`}
            {mangel.berichtsnummer ? ` (Bericht ${mangel.berichtsnummer})` : ''}
          </div>
        </div>

        <div className="text-right">
          {mangel.gesperrt && (
            <div className="marke border-2 border-stufe-3 text-stufe-3">gesperrt</div>
          )}
          {mangel.frist_am && (
            <div className="mt-1 text-base font-semibold">
              Frist: {datumDeutsch(mangel.frist_am)}
            </div>
          )}
        </div>
      </div>

      {mangel.foto_ids.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2">
          {mangel.foto_ids.map((id) => (
            <a key={id} href={`/api/portal/fotos/${id}`} target="_blank" rel="noreferrer">
              <img
                src={`/api/portal/fotos/${id}`}
                alt="Mangelfoto"
                className="h-24 w-32 rounded border-2 border-slate-200 object-cover"
                loading="lazy"
              />
            </a>
          ))}
        </div>
      )}

      {!mangel.meldung_erwartet ? (
        /* Hier steht bewusst ein Satz und kein fehlender Knopf.

           Der Prüfer hat bei diesem Mangel festgelegt, dass er die Behebung
           bei der Nachprüfung selbst feststellt. Ließe man die Stelle
           einfach leer, suchte der Bauhof etwas, das es nicht gibt, und
           wüsste nicht, ob er handeln muss. */
        <div className="mt-3 rounded-feld border-2 border-slate-200 bg-slate-50 p-3">
          <p className="font-semibold">Keine Rückmeldung nötig</p>
          <p className="mt-1 text-base">
            Für diesen Mangel ist keine Meldung über das Portal vorgesehen. Die
            Behebung wird bei der nächsten Prüfung vor Ort festgestellt. Beheben
            Sie den Mangel bitte trotzdem
            {mangel.frist_am ? ' innerhalb der genannten Frist' : ''}.
          </p>
        </div>
      ) : gemeldet ? (
        <div className="mt-3 rounded-feld border-2 border-stufe-1 bg-stufe-1-grund p-3">
          <p className="font-semibold text-stufe-1">
            Behebung gemeldet am {datumDeutsch(mangel.gemeldet_am?.slice(0, 10) ?? null)}
          </p>
          {mangel.meldungstext && (
            <p className="mt-1 whitespace-pre-wrap text-base">{mangel.meldungstext}</p>
          )}
          <p className="mt-2 text-base">
            Der Mangel bleibt offen, bis der Prüfer die Behebung bei einer Nachprüfung
            bestätigt hat.
          </p>
        </div>
      ) : formular ? (
        <form onSubmit={melden} className="mt-3 space-y-3">
          <div>
            <label htmlFor={`text-${mangel.id}`}>Was wurde gemacht?</label>
            <textarea
              id={`text-${mangel.id}`}
              value={text}
              onChange={(e) => setzeText(e.target.value)}
              placeholder="Zum Beispiel: Ketten am 12.09. durch den Bauhof getauscht."
              style={{ minHeight: '72px' }}
            />
          </div>
          <p className="text-base text-slate-700">
            Ihre Meldung wird festgehalten. <strong>Bestätigt</strong> wird die Behebung
            erst durch den Prüfer bei der nächsten Nachprüfung – bis dahin gilt der Mangel
            weiter, und ein gesperrtes Gerät bleibt gesperrt.
          </p>

          <Fehlerhinweis fehler={fehler} />

          <div className="flex gap-2">
            <button type="submit" className="knopf-haupt" disabled={laeuft}>
              {laeuft ? 'Meldet …' : 'Behebung melden'}
            </button>
            <button
              type="button"
              className="knopf-still"
              onClick={() => setzeFormular(false)}
            >
              Abbrechen
            </button>
          </div>
        </form>
      ) : (
        <button
          type="button"
          className="knopf-zweit mt-3"
          onClick={() => setzeFormular(true)}
        >
          Behebung melden
        </button>
      )}
    </li>
  )
}
